import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faBan, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import { StaticTh } from './SortableTh';
import type { PartsRowStatus } from '../data/timEInventoryList';
import './PartsInventoryTable.css';

/** The columns this table shows; both the TIM-E and BIM-E parts rows satisfy it. */
export type PartsTableRow = {
  product: string;
  available: string;
  fullyAssembled: string;
  notAssembled: string;
  replacementCostPerItem: string;
  lowStockBuffer?: number | null;
  status?: PartsRowStatus;
};

export interface PartsInventoryTableProps {
  title: string;
  rows: PartsTableRow[];
  /** Index is into `rows`, so the caller can patch its own typed array. */
  onSaveRow: (index: number, patch: { available: string; lowStockBuffer: number | null }) => void;
  onSetStatus: (index: number, status: PartsRowStatus) => void;
  /** Part name to highlight and scroll to, e.g. when arriving from the dashboard low-stock card. */
  highlightPart?: string;
  /** True while the initial fetch is outstanding: rows are shown but cannot be changed. */
  readOnly?: boolean;
}

function parseCount(value: string): number {
  return Math.max(0, parseInt(String(value).trim(), 10) || 0);
}

function isVoid(row: PartsTableRow): boolean {
  return row.status === 'Void';
}

/**
 * Parts inventory for a bot, shown on that bot's product page. Parts live in their own
 * table rather than the product catalog, so this is where their counts, low-stock
 * buffers and Active/Void state are managed.
 */
const PartsInventoryTable: React.FC<PartsInventoryTableProps> = ({
  title,
  rows,
  onSaveRow,
  onSetStatus,
  highlightPart,
  readOnly = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [draftAvailable, setDraftAvailable] = useState('');
  const [draftBuffer, setDraftBuffer] = useState('');
  const [voidIndex, setVoidIndex] = useState<number | null>(null);

  const highlightKey = useMemo(() => (highlightPart ?? '').trim().toLowerCase(), [highlightPart]);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  /** Scroll to the row the dashboard linked to once, not again on every later save. */
  const scrolledFor = useRef('');
  useEffect(() => {
    if (!highlightKey || collapsed || scrolledFor.current === highlightKey) return;
    const el = highlightedRowRef.current;
    if (!el) return;
    scrolledFor.current = highlightKey;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightKey, collapsed, rows]);

  const activeCount = useMemo(() => rows.filter((r) => !isVoid(r)).length, [rows]);

  const openEdit = useCallback((index: number) => {
    const row = rows[index];
    setEditIndex(index);
    setDraftAvailable(row.available ?? '');
    setDraftBuffer(row.lowStockBuffer != null ? String(row.lowStockBuffer) : '');
  }, [rows]);

  const closeEdit = useCallback(() => setEditIndex(null), []);

  const submitEdit = useCallback(() => {
    if (editIndex == null) return;
    const trimmed = draftBuffer.trim();
    onSaveRow(editIndex, {
      available: String(parseCount(draftAvailable)),
      lowStockBuffer: trimmed === '' ? null : Math.max(0, Math.floor(Number(trimmed) || 0)),
    });
    setEditIndex(null);
  }, [editIndex, draftAvailable, draftBuffer, onSaveRow]);

  const confirmVoid = useCallback(() => {
    if (voidIndex == null) return;
    onSetStatus(voidIndex, 'Void');
    setVoidIndex(null);
  }, [voidIndex, onSetStatus]);

  const editingRow = editIndex != null ? rows[editIndex] : null;
  const voidingRow = voidIndex != null ? rows[voidIndex] : null;

  return (
    <div className="product-table-section">
      <div className="collapsible-header">
        <div className="collapsible-header-left" onClick={() => setCollapsed((c) => !c)}>
          <h3 className="product-table-title">{title}</h3>
          <span className={`collapse-arrow${!collapsed ? ' collapse-arrow--open' : ''}`}>▶</span>
        </div>
        <span className="parts-table-count">
          {activeCount} active{rows.length > activeCount ? `, ${rows.length - activeCount} void` : ''}
        </span>
      </div>

      {!collapsed &&
        (rows.length === 0 ? (
          <p className="table-empty">No parts recorded.</p>
        ) : (
          <>
            <p className="parts-table-hint">
              A part shows on the dashboard low-stock card while its available count is under its
              low-stock buffer. Clear the buffer to stop that, or void the part to retire it without
              losing the record.
            </p>
            <div className="product-table-wrapper">
              <table className="product-table parts-table">
                <thead>
                  <tr>
                    <StaticTh>Part</StaticTh>
                    <StaticTh className="parts-table-num">Available</StaticTh>
                    <StaticTh className="parts-table-num">Assembled</StaticTh>
                    <StaticTh className="parts-table-num">Not assembled</StaticTh>
                    <StaticTh className="parts-table-num">Low-stock buffer</StaticTh>
                    <StaticTh className="parts-table-num">Replacement cost</StaticTh>
                    <StaticTh>Status</StaticTh>
                    <StaticTh className="product-table-actions-header">Actions</StaticTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const voided = isVoid(row);
                    const buffer = row.lowStockBuffer;
                    const isLow =
                      !voided && buffer != null && buffer > 0 && parseCount(row.available) < buffer;
                    const highlighted =
                      !!highlightKey && row.product.trim().toLowerCase() === highlightKey;
                    return (
                      <tr
                        key={`${row.product}-${index}`}
                        ref={highlighted ? highlightedRowRef : undefined}
                        className={[
                          voided ? 'parts-row--void' : '',
                          highlighted ? 'parts-row--highlighted' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <td className="parts-table-name">
                          {row.product}
                          {isLow && <span className="parts-low-pill">Low stock</span>}
                        </td>
                        <td className="parts-table-num">{parseCount(row.available)}</td>
                        <td className="parts-table-num">{row.fullyAssembled || '—'}</td>
                        <td className="parts-table-num">{row.notAssembled || '—'}</td>
                        <td className="parts-table-num">
                          {buffer != null && buffer > 0 ? buffer : '—'}
                        </td>
                        <td className="parts-table-num">{row.replacementCostPerItem || '—'}</td>
                        <td>
                          <span
                            className={`parts-status-pill parts-status-pill--${voided ? 'void' : 'active'}`}
                          >
                            {voided ? 'Void' : 'Active'}
                          </span>
                        </td>
                        <td className="product-table-actions-cell">
                          <div className="product-row-actions">
                            {readOnly ? (
                              <span
                                className="product-row-actions-locked"
                                title="Parts are still loading from the server"
                              >
                                —
                              </span>
                            ) : voided ? (
                              <button
                                type="button"
                                className="product-row-btn product-row-btn--restore"
                                data-tooltip="Restore"
                                onClick={() => onSetStatus(index, 'Active')}
                                aria-label={`Restore ${row.product}`}
                              >
                                <FontAwesomeIcon icon={faRotateLeft} />
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="product-row-btn product-row-btn--edit"
                                  data-tooltip="Edit"
                                  onClick={() => openEdit(index)}
                                  aria-label={`Edit ${row.product}`}
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                <button
                                  type="button"
                                  className="product-row-btn product-row-btn--void"
                                  data-tooltip="Void"
                                  onClick={() => setVoidIndex(index)}
                                  aria-label={`Void ${row.product}`}
                                >
                                  <FontAwesomeIcon icon={faBan} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ))}

      <Modal isOpen={editIndex != null} onClose={closeEdit} title={`Edit ${editingRow?.product ?? 'part'}`} narrow>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="parts-edit-available">
              Available
            </label>
            <input
              id="parts-edit-available"
              type="number"
              min={0}
              className="form-input"
              value={draftAvailable}
              onChange={(e) => setDraftAvailable(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="parts-edit-buffer">
              Low-stock buffer
            </label>
            <input
              id="parts-edit-buffer"
              type="number"
              min={0}
              className="form-input"
              placeholder="Leave blank for none"
              value={draftBuffer}
              onChange={(e) => setDraftBuffer(e.target.value)}
            />
            <span className="form-hint">
              Blank keeps this part off the dashboard low-stock card entirely.
            </span>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={closeEdit}>
            Cancel
          </button>
          <button type="button" className="save-button" onClick={submitEdit}>
            Save
          </button>
        </div>
      </Modal>

      <Modal isOpen={voidIndex != null} onClose={() => setVoidIndex(null)} title="Void part" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 0.5rem', color: '#374151', fontSize: '0.9375rem' }}>
            Void <strong>{voidingRow?.product}</strong>? It stops appearing on the dashboard low-stock
            card and on the monthly inventory report.
          </p>
          <p style={{ margin: 0, color: '#8A8F93', fontSize: '0.875rem' }}>
            The row stays here with its counts intact, so you can restore it later.
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setVoidIndex(null)}>
            Cancel
          </button>
          <button type="button" className="client-delete-confirm-btn" onClick={confirmVoid}>
            Void part
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PartsInventoryTable;
